import type { BlockPeriodDataApiType } from '@naturehouse/design-system/components/atoms/period/models/BlockPeriodModel';
import type HttpError from '@naturehouse/nh-essentials/lib/requests/HttpError';
import CollectionBase from '../../common/CollectionBase';
import { PeriodStorageCollectionType } from '../app/PeriodStorageManager';
import type { BasePeriodCollectionParamsType } from './BasePeriodCollectionParams.type';

export type PeriodBlockCollectionParamsType = BasePeriodCollectionParamsType & {
    blockId?: string;
};

export type PeriodBlockCollectionDataType = Omit<BlockPeriodDataApiType, 'id' | 'type'>;

export default class PeriodBlockCollection extends CollectionBase<
    PeriodBlockCollectionDataType,
    PeriodBlockCollectionParamsType
> {
    protected name = PeriodStorageCollectionType.BLOCK;

    static #instance: PeriodBlockCollection | null = null;

    public static getInstance(): PeriodBlockCollection {
        if (this.#instance === null) {
            this.#instance = new PeriodBlockCollection();
        }

        return this.#instance;
    }

    public async retrieve<RT = BlockPeriodDataApiType[]>(
        params?: PeriodBlockCollectionParamsType
    ): Promise<RT | void> {
        return super.retrieve<RT>(params);
    }

    public add<RT = AjaxResponse>(
        data: PeriodBlockCollectionDataType,
        params: PeriodBlockCollectionParamsType
    ): Promise<RT | void> {
        return super.add<AjaxResponse>(data, params) as Promise<RT | void>;
    }

    public async delete(params?: PeriodBlockCollectionParamsType): Promise<void> {
        return super.delete(params) as Promise<void>;
    }

    public async update<RT = AjaxResponse>(
        data: PeriodBlockCollectionDataType,
        params: PeriodBlockCollectionParamsType
    ): Promise<RT | void> {
        return super.update<AjaxResponse>(data, params) as Promise<RT | void>;
    }

    protected endpoint(params: PeriodBlockCollectionParamsType): string {
        if (!params || !params.houseId) {
            throw new Error('houseId is required');
        }

        const baseUrl = `/api/houses/${params.houseId}/periods/blocks`;

        if (params.start && params.end) {
            return `${baseUrl}?start=${params.start}&end=${params.end}`;
        }

        return baseUrl;
    }

    protected updateEndpoint(params?: PeriodBlockCollectionParamsType | undefined): string {
        if (!params || !params.houseId || !params.blockId) {
            throw new Error('houseId & blockID are required');
        }

        return `/api/houses/${params.houseId}/periods/blocks/${params.blockId}`;
    }

    protected onError(error: HttpError<AjaxResponseError>): void {
        throw new Error(error.body?.message);
    }
}
